import { useEffect, useState, ReactNode } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useWishlist, type WishlistInput } from '@/data/hooks/useWishlist';
import { useAuth } from '@/contexts/AuthContext';
import type { WishlistItem, WishlistUrgency, WishlistStatus, WishlistSourcingStatus } from '@/types';
import { SOURCING_STATUS_LABELS } from '@/types';

const URGENCIES: { value: WishlistUrgency; label: string }[] = [
  { value: 'hot', label: 'Hot' },
  { value: 'warm', label: 'Warm' },
  { value: 'cold', label: 'Cold' },
];

const SOURCING_OPTIONS: WishlistSourcingStatus[] = [
  'searching', 'incoming', 'located', 'notified', 'appointment_set',
];

const schema = z.object({
  customer_name: z.string().trim().min(1, 'Customer name is required').max(100),
  customer_phone: z.string().trim().max(40).optional(),
  customer_email: z.string().trim().max(255).optional(),
  make: z.string().trim().min(1, 'Make is required').max(60),
  model: z.string().trim().max(60).optional(),
  year_min: z.number().int().min(1950).max(2100).nullable().optional(),
  year_max: z.number().int().min(1950).max(2100).nullable().optional(),
  max_price: z.number().min(0).max(10_000_000).nullable().optional(),
  color: z.string().trim().max(40).optional(),
  notes: z.string().trim().max(500).optional(),
  urgency: z.enum(['hot', 'warm', 'cold']),
  sourcing_status: z.enum(['searching', 'incoming', 'located', 'notified', 'appointment_set']),
}).refine(d => !d.year_min || !d.year_max || d.year_min <= d.year_max, {
  message: 'Year min must be ≤ year max', path: ['year_max'],
});

interface Props {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  /** Existing item to edit; omit for create */
  item?: WishlistItem;
  /** Pre-fill values when creating from a deal */
  prefill?: Partial<WishlistInput>;
}

export function WishlistSheet({ trigger, open: openProp, onOpenChange, item, prefill }: Props) {
  const { mode } = useAuth();
  const { create, update, remove } = useWishlist();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    make: '', model: '', year_min: '', year_max: '', max_price: '',
    color: '', notes: '', urgency: 'warm' as WishlistUrgency,
    sourcing_status: 'searching' as WishlistSourcingStatus,
  });

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        customer_name: item.customer_name,
        customer_phone: item.customer_phone ?? '',
        customer_email: item.customer_email ?? '',
        make: item.make,
        model: item.model ?? '',
        year_min: item.year_min?.toString() ?? '',
        year_max: item.year_max?.toString() ?? '',
        max_price: item.max_price?.toString() ?? '',
        color: item.color ?? '',
        notes: item.notes ?? '',
        urgency: item.urgency,
        sourcing_status: item.sourcing_status,
      });
    } else if (prefill) {
      setForm(f => ({
        ...f,
        customer_name: prefill.customer_name ?? '',
        customer_phone: prefill.customer_phone ?? '',
        customer_email: prefill.customer_email ?? '',
        make: prefill.make ?? '',
        model: prefill.model ?? '',
      }));
    }
  }, [open, item, prefill]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'demo') {
      toast.info('Demo mode — sign in to a real dealership to save wishlists');
      setOpen(false);
      return;
    }
    const parsed = schema.safeParse({
      ...form,
      year_min: form.year_min ? Number(form.year_min) : null,
      year_max: form.year_max ? Number(form.year_max) : null,
      max_price: form.max_price ? Number(form.max_price) : null,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Check your inputs');
      return;
    }
    setSubmitting(true);
    try {
      const payload: WishlistInput = {
        ...(parsed.data as WishlistInput),
        deal_id: prefill?.deal_id ?? item?.deal_id ?? null,
      };
      if (item) {
        await update(item.id, payload);
        toast.success('Wishlist updated');
      } else {
        await create(payload);
        toast.success('Wishlist saved');
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not save wishlist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatus = async (status: WishlistStatus) => {
    if (!item) return;
    setSubmitting(true);
    try {
      await update(item.id, { status });
      toast.success(status === 'fulfilled' ? 'Marked as found' : 'Wishlist dropped');
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    if (!confirm('Delete this wishlist entry? This cannot be undone.')) return;
    setSubmitting(true);
    try {
      await remove(item.id);
      toast.success('Wishlist deleted');
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not delete');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {trigger && (
        <SheetTrigger asChild>
          {trigger}
        </SheetTrigger>
      )}
      {!trigger && !openProp && (
        <SheetTrigger asChild>
          <Button size="lg" variant="outline" className="w-full text-base h-14 gap-2">
            <Plus className="h-5 w-5" /> Add Wishlist
          </Button>
        </SheetTrigger>
      )}
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{item ? 'Edit wishlist' : 'New wishlist'}</SheetTitle>
          <SheetDescription>
            Capture what your customer is looking for so nothing slips when a match comes in.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Customer name *</label>
            <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required autoFocus placeholder="Sarah Chen" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone</label>
              <Input type="tel" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} placeholder="(519) 555-0123" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} placeholder="optional" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Make *</label>
              <Input value={form.make} onChange={e => setForm({ ...form, make: e.target.value })} required placeholder="Land Rover" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Model</label>
              <Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="Defender" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Year min</label>
              <Input type="number" inputMode="numeric" value={form.year_min} onChange={e => setForm({ ...form, year_min: e.target.value })} placeholder="2022" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Year max</label>
              <Input type="number" inputMode="numeric" value={form.year_max} onChange={e => setForm({ ...form, year_max: e.target.value })} placeholder="optional" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Max $</label>
              <Input type="number" inputMode="numeric" value={form.max_price} onChange={e => setForm({ ...form, max_price: e.target.value })} placeholder="65000" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Color / trim</label>
            <Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="White, low miles" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Urgency</label>
            <div className="grid grid-cols-3 gap-2">
              {URGENCIES.map(u => (
                <button
                  key={u.value}
                  type="button"
                  onClick={() => setForm({ ...form, urgency: u.value })}
                  className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                    form.urgency === u.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-secondary text-secondary-foreground border-border hover:bg-accent'
                  }`}
                >
                  {u.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes</label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Trade-in for 2018 XE, wants delivery before May" />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Sourcing status</label>
            <div className="grid grid-cols-5 gap-1.5">
              {SOURCING_OPTIONS.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setForm({ ...form, sourcing_status: s })}
                  className={`px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors ${
                    form.sourcing_status === s
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-secondary text-secondary-foreground border-border hover:bg-accent'
                  }`}
                >
                  {SOURCING_STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          <SheetFooter className="flex-col gap-2 sm:flex-col">
            <div className="flex gap-2 w-full">
              <Button type="button" variant="ghost" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={submitting}>
                {submitting ? 'Saving…' : item ? 'Save' : 'Add wishlist'}
              </Button>
            </div>
            {item && (
              <div className="flex gap-2 w-full pt-2 border-t border-border/50">
                <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={() => handleStatus('fulfilled')} disabled={submitting}>
                  Mark found
                </Button>
                <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={() => handleStatus('dropped')} disabled={submitting}>
                  Drop
                </Button>
                <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={handleDelete} disabled={submitting}>
                  Delete
                </Button>
              </div>
            )}
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}